<template>
  <div class="animation page">
    <subpage-layout :theme="'dark'" :name="this.$route.name">
      <template v-slot:hero-image>
        <img src="../assets/img/hero-img-6.jpg" />
      </template>
      <template v-slot:hero-title>Animation</template>
      <template v-slot:description>
        <h2>Animation</h2>
        <p>
          I find the merging of art with technology endlessly fascinating.
          Visualizing data and creating beautiful interactions is one of my
          passions. I am still amazed at how much you can do with a little CSS
          and Javascript and how exciting it is to see data come to life.
        </p>
      </template>

      <template v-slot:skills>
        <featured-temp :kind="'sm'">
          <template v-slot:featured-title>Skills</template>
          <template v-slot:default>
            <ul>
              <li>CSS Animations</li>
              <li>Javascript Animations</li>
              <li>Greensock Animation Platform</li>
              <li>HTML5 Canvas</li>
              <li>D3.js</li>
              <li>ScrollMagic</li>
              <li>Blender</li>
              <li>Three.js</li>
              <li>JQuery</li>
            </ul>
          </template>
        </featured-temp>
      </template>

      <template v-slot:pics>
        <project-picture v-intersect="planetsIntersect" :canvas="true">
          <template v-slot:pic>
            <transition name="anim-change">
              <canvas-stars
                :animProg="planetsAnimIntersect"
                :twinkleRate="twinkleRate"
                :numStars="numStars"
                :key="numStars + '_' + twinkleRate"
              />
            </transition>

            <scale-tween :zoom="zoom">
              <planets-anim :animProg="planetsAnimIntersect" />
            </scale-tween>

            <captain :zoom="zoom"></captain>

            <div class="stars-control">
              <div class="slider-row">
                <div class="slider-label">Number of Stars</div>
                <v-slider
                  v-model.number="numStars"
                  :dark="true"
                  :color="'#fff'"
                  :hide-details="true"
                  min="0"
                  max="1000"
                />

                <span><anim-number :dataNumber="numStars"></anim-number></span>
              </div>

              <div class="slider-row">
                <div class="slider-label">Twinkle Rate</div>
                <v-slider
                  :dark="true"
                  :color="'#fff'"
                  :hide-details="true"
                  v-model.number="twinkleRate"
                />

                <span
                  ><anim-number :dataNumber="twinkleRate"></anim-number
                ></span>
              </div>

              <div class="slider-row">
                <div class="slider-label">Zoom</div>

                <v-slider
                  v-model.number="zoom"
                  min="0"
                  max="100"
                  :dark="true"
                  :color="'#fff'"
                  :hide-details="true"
                />
                <span><anim-number :dataNumber="zoom"></anim-number></span>
              </div>
            </div>
          </template>
          <template v-slot:label>
            <div class="distance">
              Distance:
              <anim-number
                :dataNumber="zoom"
                :map="[0, 100, 9460730472, 6000]"
              ></anim-number
              >km
            </div>

            <p>Animating an imaginary universe with canvas and css</p>
          </template>
        </project-picture>

        <project-picture
          v-intersect="bioIntersect"
          class="canvas-size"
          :canvas="true"
        >
          <template v-slot:pic>
            <canvas-creatures :animProg="bioLumenIntersect"></canvas-creatures>
            <canvas-strings :animProg="bioLumenIntersect"></canvas-strings>
          </template>
          <template v-slot:label>
            <p>
              HTML Canvas animation inspired by deep-sea creatures and
              bioluminescence
            </p>
          </template>
        </project-picture>
      </template>
    </subpage-layout>
  </div>
</template>

<script>
import subpageLayout from "@/components/SubpageLayout";
import captain from "@/components/Captain";
import projectPicture from "@/components/ProjectPicture";
import featuredTemp from "@/components/FeaturedTemp";
import canvasCreatures from "@/components/CanvasCreatures";
import canvasStars from "@/components/CanvasStars";
import canvasStrings from "@/components/CanvasStrings";
import planetsAnim from "@/components/PlanetsAnim";
import scaleTween from "@/components/ScaleTween";
import animNumber from "@/components/AnimNumber";

export default {
  name: "animation",

  components: {
    subpageLayout,
    captain,
    canvasStrings,
    projectPicture,
    featuredTemp,
    canvasCreatures,
    canvasStars,
    planetsAnim,
    scaleTween,
    animNumber,
  },

  data() {
    return {
      bioLumenIntersect: false,
      planetsAnimIntersect: false,
      numStars: 20,
      twinkleRate: 20,
      zoom: 0,
    };
  },

  beforeDestoy() {
    this.bioLumenIntersect = false;
    this.planetsAnimIntersect = false;
  },
  methods: {
    bioIntersect(entries, observer) {
      // More information about these options
      // is located here: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
      this.bioLumenIntersect = entries[0].isIntersecting;
      //console.log(this.bioLumenIntersect);
    },
    planetsIntersect(entries, observer) {
      // More information about these options
      // is located here: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
      this.planetsAnimIntersect = entries[0].isIntersecting;
    },
  },
  watch: {
    planetsAnimIntersect(nv) {
      if (nv === false) {
        this.twinkleRate = 20;
        this.zoom = 0;
      }
    },
  },
};
</script>

<style lang="scss">
.animation .subpage-hero-bg {
  background-color: black;
  mix-blend-mode: multiply;
}

.animation .img-wrapper {
  overflow: hidden;
  padding: 0;
}

.stars-control {
  position: relative;
  display: none;
  left: 20px;
  top: 20px;
  color: rgba(255, 255, 255, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 20px;
  padding: 0.75rem;
  transition: all 0.25s ease-in-out;
  @include breakpoint(md) {
    display: inline-block;
  }

  &:hover {
    border: 2px solid rgba(255, 255, 255, 0.6);
  }

  span {
    display: inline-block;
    padding: 0.5rem;
    min-width: 45px;
  }
  .slider-label {
    color: #fff;
    width: 100%;
  }
  .slider-row {
    display: flex;
    flex-wrap: wrap;

    &:hover {
      .anim-number {
        color: rgba(255, 255, 255, 1);
      }
    }
    .anim-number {
      color: rgba(255, 255, 255, 0.4);
      transition: color 0.25s ease-in-out;
    }
  }
}

.slider-control {
  color: white;
}
.distance {
  display: none;
  justify-content: flex-end;
  color: rgba(255, 255, 255, 0.8);
  font-size: 85%;
  font-style: oblique;
  @include breakpoint(md) {
    display: flex;
  }

  .anim-number {
    padding-left: 10px;
    padding-right: 0.5ch;
    font-size: 20px;
    font-weight: 800;
  }
  span {
    font-size: 2rem;
  }
}

.anim-change-enter-active,
.anim-change-leave-active {
  transition-duration: 1s;
  transition-property: opacity;
  transition-timing-function: ease-out;
}

.anim-change-enter,
.anim-change-leave-active {
  opacity: 0;
}
</style>
